import type { ReactNode } from 'react';
import { useAuth, type UserRole } from '@/contexts/AuthContext';

/**
 * DREAM-5 AC8 — UI only shows actions permitted by the user's role.
 *
 * Server-side `requireRole()` is the source of truth (and the only line of
 * security). This component is **UX** — it stops users from clicking buttons
 * that will 403. If you forget to wrap a button, the backend still blocks
 * the action; the user just sees the error instead of nothing.
 *
 * Permission matrices live in `src/lib/permissions.ts` — pass the canonical
 * role list from there so frontend stays in sync as the backend permits more
 * roles.
 *
 *   <RoleGate roles={CAN_CREATE_SUBJECT}>
 *     <button onClick={...}>New subject</button>
 *   </RoleGate>
 */

export interface RoleGateProps {
  /** Roles permitted to see the children. Match against `user.role`. */
  roles: ReadonlyArray<UserRole>;
  /** Render if allowed. */
  children: ReactNode;
  /** Render if NOT allowed. Defaults to `null` (hide). */
  fallback?: ReactNode;
}

/**
 * DELIBERATELY FREE OF REACT-QUERY.
 *
 * An earlier pass taught this component to resolve a backend operation itself,
 * which meant every RoleGate anywhere subscribed to /me/permissions — and
 * promptly broke seven tests that render gated UI without a QueryClient. It is
 * the most widely used primitive in the app; widening its dependencies for the
 * handful of call sites that need an operation is the wrong trade.
 *
 * Where the RBAC grid should decide, the CALL SITE asks:
 *
 *   const perms = useMyPermissions();
 *   perms.holdsOperation('team.invite', CAN_INVITE_TEAM)
 *
 * which keeps the dependency where it is actually used.
 */
export function RoleGate({ roles, children, fallback = null }: RoleGateProps) {
  const { user } = useAuth();
  if (!user) return <>{fallback}</>;
  if (!roles.includes(user.role as UserRole)) return <>{fallback}</>;
  return <>{children}</>;
}

/** Hook form for places that need a boolean (e.g. disable instead of hide). */
export function useHasRole(roles: ReadonlyArray<UserRole>): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return roles.includes(user.role as UserRole);
}
